import React, { useEffect, useRef, useCallback } from 'react';
import { useWidgetStore } from '../hooks';
import { useWidgetConfig } from '../context';
import { PanelHeader } from './PanelHeader';
import { TutorialsTab } from './tutorials/TutorialsTab';
import { DocsTab } from './docs/DocsTab';
import { WhatsNewTab } from './whats-new/WhatsNewTab';
import { AssistantTab } from './assistant/AssistantTab';

/**
 * Slide-in panel rendered from the right edge of the viewport.
 * Desktop: resizable from 320-600px. Mobile (<768px): full-screen overlay.
 *
 * Accessibility:
 *  - role="dialog" + aria-modal for screen readers
 *  - Focus trap: Tab cycles within the panel while open
 *  - Escape key closes the panel
 */
export function SupportWidgetPanel() {
  const config = useWidgetConfig();
  const isOpen = useWidgetStore((s) => s.isOpen);
  const close = useWidgetStore((s) => s.close);
  const panelWidth = useWidgetStore((s) => s.panelWidth);
  const setWidth = useWidgetStore((s) => s.setWidth);
  const activeTab = useWidgetStore((s) => s.activeTab);

  const panelRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const minW = config.minWidth ?? 320;
  const maxW = config.maxWidth ?? 600;
  const zIndex = config.zIndex ?? 50;

  /* Save & restore focus on open/close */
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
      // Focus the panel itself so keyboard users land inside
      requestAnimationFrame(() => {
        panelRef.current?.focus();
      });
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [isOpen]);

  /* Close on Escape */
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, close]);

  /* Focus trap: keep Tab cycling within the panel */
  useEffect(() => {
    if (!isOpen) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;

      const focusable = panel.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first || !panel.contains(document.activeElement)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last || !panel.contains(document.activeElement)) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  /* Drag-to-resize logic */
  const onDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragRef.current = { startX: e.clientX, startWidth: panelWidth };

      const onMove = (ev: MouseEvent) => {
        if (!dragRef.current) return;
        const delta = dragRef.current.startX - ev.clientX;
        const next = Math.min(maxW, Math.max(minW, dragRef.current.startWidth + delta));
        setWidth(next);
      };
      const onUp = () => {
        dragRef.current = null;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [panelWidth, setWidth, minW, maxW],
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'tutorials':
        return <TutorialsTab />;
      case 'docs':
        return <DocsTab />;
      case 'releases':
        return <WhatsNewTab />;
      case 'charlotte':
        return <AssistantTab />;
      default:
        return null;
    }
  };

  return (
    <>
      {/* Backdrop for mobile -- click to close */}
      {isOpen && (
        <div
          onClick={close}
          aria-hidden="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'var(--sw-overlay-bg, rgba(0,0,0,0.7))',
            zIndex: zIndex - 1,
            opacity: isOpen ? 1 : 0,
            transition: 'opacity 0.2s ease',
          }}
        />
      )}

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${config.productName} support`}
        tabIndex={-1}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: `min(${panelWidth}px, 100vw)`,
          maxWidth: '100vw',
          zIndex,
          background: 'var(--sw-bg-panel, #1a1a2e)',
          color: 'var(--sw-text-primary, #e0e0e0)',
          fontFamily: 'var(--sw-font-family)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.4)',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          outline: 'none',
        }}
      >
        {/* Drag handle (left edge) -- desktop only */}
        <div
          onMouseDown={onDragStart}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: '6px',
            cursor: 'col-resize',
            zIndex: 1,
          }}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panel"
          tabIndex={0}
        />

        <PanelHeader />

        {/* Scrollable content area */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {renderTabContent()}
        </div>
      </div>
    </>
  );
}
